import { useState } from "react";
import axios from "axios";
import { useCallback } from "react";
import { ClaimValidationAxiosInstance } from "../../../Network/ClaimValidation";
import { ObservationRow, CodeOption, ActivityRow, AllowedActivityType, ACTIVITY_TYPE_TO_CODE, CODE_TO_ACTIVITY_TYPE } from "../../../interface/ClaimValdation";
import ClaimCodeMultiSelect from "../../../component/ClaimCodeMultiSelect";
import ActivityTypeSingleSelector from "../../../component/ActivityTypeSelector";
import styles from './ClaimValidation.module.css';
import { Toaster } from "../../../components/ui/toaster";
import { useToast } from "../../../hooks/use-toast";

// style
const GLOBAL_VARIABLES = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
:root {
 --cv-bg:        #f0f4f8;
 --cv-surface:        #ffffff;
 --cv-surface-2:    #f7f9fc;
 --cv-border:       #dde3ec;
 --cv-border-focus:#3b6fd4;
 --cv-text:         #1a2235;
 --cv-muted:        #6b7a99;
 --cv-accent:       #3b6fd4;
 --cv-accent-soft: #eef2fb;
 --cv-success:      #22c55e;
 --cv-danger:       #ef4444;
 --cv-danger-soft: #fef2f2;
 --cv-warn-soft:    #fffbeb;
 --cv-radius:       10px;
 --cv-radius-sm:    6px;
 --cv-shadow:       0 1px 3px rgba(26,34,53,0.08), 0 4px 16px rgba(26,34,53,0.06);
 --cv-shadow-focus:0 0 0 3px rgba(59,111,212,0.18);
 --cv-font:         'DM Sans', sans-serif;
 --cv-mono:         'DM Mono', monospace;
 --cv-transition:   all 0.18s cubic-bezier(.4,0,.2,1);
}
`;

const VariablesInjector = () => <style dangerouslySetInnerHTML={{ __html: GLOBAL_VARIABLES }} />;

const CvLabel = ({ children }: { children: React.ReactNode }) => (
<span className={styles.cvLabel}>{children}</span>
);


const CvInput = ({ type = "text", value, onChange, disabled, placeholder, onKeyDown }: {
type?: string; value?: string | number;
onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
disabled?: boolean; placeholder?: string;
onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}) => (
<input 
  className={styles.cvInput} 
  type={type} 
  value={value} 
  onChange={onChange} 
  disabled={disabled} 
  placeholder={placeholder} 
  onKeyDown={onKeyDown}
/>
);

const CvSelect = ({ value, onChange, disabled, children }: {
value?: string;
onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
disabled?: boolean; children: React.ReactNode;
}) => (
<div className={styles.cvSelectWrap}>
 <select className={styles.cvSelect} value={value} onChange={onChange} disabled={disabled}>
   {children}
 </select>
</div>
);


// ─── Main Component ───────────────────────────────────────────────────────────
const ClaimValidation = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;
  const [isLoading, setIsLoading] = useState(false);


  // ── Step 1: Patient info ──────────────────────────────────────────────────
  const [patientName, setPatientName] = useState("");
  const [patientGender, setPatientGender] = useState<"MALE" | "FEMALE" | undefined>(undefined);
  const [patientIdentifierType, setPatientIdentifierType] = useState<"NATIONAL_ID" | "PASSPORT" | "MEMBER_ID" | undefined>(undefined);
  const [patientDateOfBirth, setPatientDateOfBirth] = useState("");
  const [patientIdentifierValue, setPatientIdentifierValue] = useState("");
  const [claimId, setClaimId] = useState("");
  const [claimDate, setClaimDate] = useState("");

  // ── Step 2: Insurance & provider ─────────────────────────────────────────
  const [tpaOption, setTpaOption] = useState<CodeOption[]>([]);
  const [payerOption, setPayerOption] = useState<CodeOption[]>([]);
  const [network, setNetwork] = useState<CodeOption[]>([]);
  const [policyNameOption, setPolicyNameOption] = useState<CodeOption[]>([]);
  const [doctorLicenseNumber, setDoctorLicenseNumber] = useState<CodeOption[]>([]);
  const [policyIdOption, setPolicyIdOption] = useState<CodeOption[]>([]);
  const [specialityName, setSpecialityName] = useState<CodeOption[]>([]);
  const [encounterType, setEncounterType] = useState<number>(1);
  const [providerId, setProviderId] = useState("");

  // ── Step 3: Diagnosis ────────────────────────────────────────────────     
  const [primaryDiagnosis, setPrimaryDiagnosis] = useState<CodeOption | null>(null);
  const [secondaryDiagnosis, setSecondaryDiagnosis] = useState<CodeOption[]>([]);
  const [diagnosisType, setDiagnosisType] = useState<"primary" | "secondary">("primary");
  const [selectedIcd, setSelectedIcd] = useState<CodeOption[]>([]);
  const [diagnosisError, setDiagnosisError] = useState<string | null>(null);

  // ── Step 4: Activities ────────────────────────────────────────────────    
  const [activities, setActivities] = useState<ActivityRow[]>([]);
  const [activityError, setActivityError] = useState("");
  const [currentObs, setCurrentObs] = useState<ObservationRow>({ Type: "", Code: "", Value: "", ValueType: "" });
  
  const emptyActivity = (): ActivityRow & { toothNumbers?: string[]; currentToothInput?: string } => ({
    ActivityId: "",        
    activityCode: "",    
    activityType: 0,   
    quantity: 1,         
    preApprovalNumber: "",
    observations: [],
    hasSupportingDocuments: false,
    toothNumbers: [], 
    currentToothInput: "",
  });
  
  const [currentActivityFile, setCurrentActivityFile] = useState<File | null>(null);
  const [currentActivity, setCurrentActivity] = useState<any>(emptyActivity());
  const [selectedActivityCodes, setSelectedActivityCodes] = useState<CodeOption[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // ── Results ───────────────────────────────────────────────────────────────
  const [validationResults, setValidationResults] = useState<any[]>([]);
  const [responseMessage, setResponseMessage] = useState("");

  const stepNames = ["Claim & Patient ", "Insurance ", "Diagnosis", "Activities", "Results"];

  // =====================================
  // Error Handling
  // =====================================
  const { toast } = useToast();
  const getApiErrorMessage = (error: any): string => {
    if (!error) return "Unknown error";
    const errorData = error.response?.data?.error;
    if (errorData?.details?.length > 0) return errorData.details[0].message;
    if (errorData?.message) return errorData.message;
    if (error.response?.data?.message) return error.response.data.message;
    return error.message || "An unexpected error occurred.";
  };

  // =====================================
  // Fetch Configure Level & Options
  // =====================================
  const fetchDoctorOptions = async (searchTerm: string) => {
    const response = await ClaimValidationAxiosInstance.get("doctors/names", {
      params: { query: searchTerm },
    });
    const items = response.data?.data || [];
    return {
      data: items.map((item: any) => ({
        value: item.licenseNumber,
        label: `(${item.licenseNumber}) ${item.name}`, 
        realId: item.id 
      })),
      total: items.length,
    };
  };

  const fetchSpecialityOptions = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) return { data: [], total: 0 };
    try {
      const response = await ClaimValidationAxiosInstance.get("specialties/codes", {
        params: { query: searchTerm },
      });
      const items = response.data?.data || [];
      return {
        data: items.map((item: any) => ({
          value: item.id,
          label: `${item.name || ''}`,  
          code: item.code || '',        
          realId: item.id 
        })),
        total: items.length,
      };
    } catch (error) { return { data: [], total: 0 }; }
  };

  const handlePolicyNameChange = (selectedOptions: CodeOption[]) => {
    const latestSelection = selectedOptions.length > 0 ? [selectedOptions[selectedOptions.length - 1]] : [];
    setPolicyNameOption(latestSelection);
    if (latestSelection.length > 0) {
      const selectedItem = latestSelection[0];
      setPolicyIdOption([
        {
          value: selectedItem.value,
          label: selectedItem.code || "", 
          code: selectedItem.label || "", 
          realId: selectedItem.realId,
        }
      ]);
    } else {
      setPolicyIdOption([]);
    }
  };

  const handlePolicyIdChange = (selectedOptions: CodeOption[]) => {
    const latestSelection = selectedOptions.length > 0 ? [selectedOptions[selectedOptions.length - 1]] : [];
    setPolicyIdOption(latestSelection);
    if (latestSelection.length > 0) {
      const selectedItem = latestSelection[0];
      setPolicyNameOption([
        {
          value: selectedItem.value,
          label: selectedItem.code || "", 
          code: selectedItem.label || "", 
          realId: selectedItem.realId,
        }
      ]);
    } else {
      setPolicyNameOption([]);
    }
  };

  const fetchPolicyNameOptions = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) return { data: [], total: 0 };
    try {
      const response = await ClaimValidationAxiosInstance.get("policies/names", {
        params: { query: searchTerm },
      });
      const items = response.data?.data || [];
      return {
        data: items.map((item: any) => ({
          value: item.id,
          label: `${item.name || ''}`,  
          code: item.policyId || '',    
          realId: item.id 
        })),
        total: items.length,
      };
    } catch (error) { return { data: [], total: 0 }; }
  };

  const fetchPolicyIdOptions = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) return { data: [], total: 0 };
    try {
      const response = await ClaimValidationAxiosInstance.get("policies/names", {
        params: { query: searchTerm },
      });
      const items = response.data?.data || [];
      return {
        data: items.map((item: any) => ({
          value: item.id,
          label: `${item.policyId || ''}`, 
          code: item.name || '',          
          realId: item.id 
        })),
        total: items.length,
      };
    } catch (error) { return { data: [], total: 0 }; }
  };

  const fetchTpaOptions = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) return { data: [], total: 0 };
    try {
      const response = await ClaimValidationAxiosInstance.get("tpas/tpa-id", {
        params: { query: searchTerm },
      });
      const items = response.data?.data || [];
      return {
        data: items.map((item: any) => ({
          value: item.tpaId, 
          label: `${item.name || ''}`,
          realId: item.id 
        })),
        total: items.length,
      };
    } catch (error) { return { data: [], total: 0 }; }
  };

  const fetchPayerOptions = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) return { data: [], total: 0 };
    try {
      const response = await ClaimValidationAxiosInstance.get("payers/names", {
        params: { query: searchTerm },
      });
      const items = response.data?.data || [];
      return {
        data: items.map((item: any) => ({
          value: item.payerId,
          label: ` ${item.name || ''}`,
          realId: item.id 
        })),
        total: items.length,
      };
    } catch (error) { return { data: [], total: 0 }; }
  };

  const fetchNetworkOptions = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) return { data: [], total: 0 };
    try {
      const response = await ClaimValidationAxiosInstance.get("networks/names", {
        params: { name: searchTerm },
      });
      const items = response.data?.data || [];
      return {
        data: items.map((item: any) => ({
          value: item.id,
          label: `${item.name || ''}`,
          realId: item.id 
        })),
        total: items.length,
      };
    } catch (error) { return { data: [], total: 0 }; }
  };

  const fetchIcdCodes = useCallback(
    async (
      searchTerm: string,
      page: number = 1,
      pageSize: number = 10
    ): Promise<{ data: CodeOption[]; total: number }> => {
      if (!searchTerm || searchTerm.trim().length < 3) {
        return { data: [], total: 0 };
      }

      try {
        const response = await ClaimValidationAxiosInstance.get("medical-code/icd/", {
          params: { search: searchTerm, page, limit: pageSize },
        });

        const items: any[] = response.data?.data?.items ?? [];
        const total: number = response.data?.data?.total ?? items.length;

        const selectedValues = [
          ...(primaryDiagnosis ? [primaryDiagnosis.value] : []),
          ...secondaryDiagnosis.map((d) => d.value),
        ];

        return {
          data: items.map((item) => {
            const isAlreadyAdded = selectedValues.includes(item.id);
            return {
              value: item.id,
              label: `${item.code} - ${item.description}`,
              code: item.code,
              realId: item.id,
              disabled: isAlreadyAdded,
            };
          }),
          total,
        };
      } catch (error) {
        toast({
          title: "Error",
          description: getApiErrorMessage(error),
          variant: "destructive",
        });

        return { data: [], total: 0 };
      }
    },
    [primaryDiagnosis, secondaryDiagnosis, toast]
  );

  const handleIcdChange = (selectedOptions: CodeOption[]) => {
    if (selectedOptions.length === 0) return;

    const icdToAdd = selectedOptions[0];
    setDiagnosisError(null);

    const isDuplicate = 
      (primaryDiagnosis?.value === icdToAdd.value) || 
      secondaryDiagnosis.some(item => item.value === icdToAdd.value);

    if (isDuplicate) {
      setDiagnosisError("This ICD code has already been added.");
      setSelectedIcd([]);
      return;
    }

    if (!primaryDiagnosis || diagnosisType === "primary") {
      setPrimaryDiagnosis({
        value: icdToAdd.value,
        label: icdToAdd.code || icdToAdd.label.split(" - ")[0],
        code: icdToAdd.code,
        realId: icdToAdd.realId
      });
      setDiagnosisType("secondary"); 
    } else {
      setSecondaryDiagnosis(prev => [
        ...prev, 
        {
          value: icdToAdd.value,
          label: icdToAdd.code || icdToAdd.label.split(" - ")[0],
          code: icdToAdd.code,
          realId: icdToAdd.realId
        }
      ]);
    }

    setSelectedIcd([]);
  };

  const fetchActivityCodes = useCallback(
    async (
      searchTerm: string,
      page: number = 1,
      pageSize: number = 10
    ): Promise<{ data: CodeOption[]; total: number }> => {
      const currentTypeNum = currentActivity.activityType;
      const currentTypeName = CODE_TO_ACTIVITY_TYPE[String(currentTypeNum)];
      const minCharsRequired = currentTypeName === "Service" ? 1 : 3;

      if (!searchTerm || searchTerm.trim().length < minCharsRequired || !currentTypeNum || currentTypeNum === 0) {
        return { data: [], total: 0 };
      }

      try {
        const response = await ClaimValidationAxiosInstance.get("medical-code/activity/", {
          params: { 
            search: searchTerm, 
            page, 
            limit: pageSize,
            type: currentTypeNum 
          },
        });

        const items: any[] = response.data?.data?.items ?? [];
        const total: number = response.data?.data?.total ?? items.length;

        const addedCodes = activities.map((act) => act.activityCode);

        return {
          data: items.map((item) => {
            const isAlreadyAdded = addedCodes.includes(item.code);
            return {
              value: item.id,
              label: `${item.code} - ${item.description}`,
              code: item.code,
              realId: item.id,
              disabled: isAlreadyAdded,
            };
          }),
          total,
        };
      } catch (error) {
        toast({
          title: "Error",
          description: getApiErrorMessage(error),
          variant: "destructive",
        });

        return { data: [], total: 0 };
      }
    },
    [currentActivity.activityType, activities, toast]
  );

  const handleActivityCodeChange = (selectedOptions: CodeOption[]) => {
    setSelectedActivityCodes(selectedOptions);
    if (selectedOptions.length > 0) {
      const selected = selectedOptions[0];
      setCurrentActivity((prev: any) => ({
        ...prev,
        activityCode: selected.code || selected.label.split(" - ")[0]
      }));
    } else {
      setCurrentActivity((prev: any) => ({
        ...prev,
        activityCode: ""
      }));
    }
  };

  const handleActivityFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
    setCurrentActivityFile(file);
    setCurrentActivity((prev: any) => ({ ...prev, hasSupportingDocuments: !!file }));
  };

  const clearActivityFile = () => {
    setCurrentActivityFile(null);
    setCurrentActivity((prev: any) => ({ ...prev, hasSupportingDocuments: false }));
  };

  const toUtcIsoDate = (dateStr: string): string => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day)).toISOString();
  };

  const toUtcDateOnly = (dateStr: string): string => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day)).toISOString().split("T")[0];
  };

  const handleNext = () => {
    if (currentStep === 3 && !primaryDiagnosis) { setDiagnosisError("Please add at least one diagnosis code before proceeding."); return; }
    if (currentStep === 4 && activities.length === 0) { setActivityError("Please add at least one activity before proceeding."); return; }
    if (currentStep < totalSteps) { setCurrentStep(currentStep + 1); setDiagnosisError(""); setActivityError(""); }
  };
  const handlePrevious = () => { if (currentStep > 1) { setCurrentStep(currentStep - 1); setDiagnosisError(""); setActivityError(""); } };
  const handleStepClick = (step: number) => { setCurrentStep(step); setDiagnosisError(""); setActivityError(""); };

  const addToothNumber = () => {
    const val = currentActivity.currentToothInput?.trim();
    if (!val) return;
    const currentList = Array.isArray(currentActivity.toothNumbers) ? currentActivity.toothNumbers : [];
    if (currentList.includes(val)) return;

    setCurrentActivity((prev: any) => ({
      ...prev,
      toothNumbers: [...currentList, val],
      currentToothInput: ""
    }));
  };

  const removeToothNumber = (toothToRemove: string) => {
    setCurrentActivity((prev: any) => ({
      ...prev,
      toothNumbers: (prev.toothNumbers || []).filter((t: string) => t !== toothToRemove)
    }));
  };

  const addActivityToTable = () => {
    if (!currentActivity.activityCode) { 
      setActivityError("Please select an activity code."); 
      return; 
    }

    const finalQuantity = currentActivity.quantity > 0 ? currentActivity.quantity : 1;
    const obsToSave = (currentObs.Type || currentObs.Code || currentObs.Value || currentObs.ValueType)
      ? [{ ...currentObs }] : [];
    
    const formattedTeeth = Array.isArray(currentActivity.toothNumbers) 
      ? currentActivity.toothNumbers.map(String).filter(Boolean) 
      : [];

    if (editingIndex !== null) {
      setActivities((prev) => 
        prev.map((act, index) => 
          index === editingIndex 
            ? { 
                ...currentActivity, 
                ActivityId: act.ActivityId,
                quantity: finalQuantity, 
                observations: obsToSave,
                toothNumbers: formattedTeeth,
              } 
            : act
        )
      );
      setEditingIndex(null);
    } else {
      const newActivityId = String(activities.length + 1);
      setActivities((prev) => [
        ...prev, 
        { 
          ...currentActivity, 
          ActivityId: newActivityId,
          quantity: finalQuantity, 
          observations: obsToSave,
          hasSupportingDocuments: currentActivity.hasSupportingDocuments,
          toothNumbers: formattedTeeth,
        }
      ]);
    }

    setCurrentActivity({ ...emptyActivity(), ActivityId: String(editingIndex !== null ? activities.length : activities.length + 2) });
    setCurrentObs({ Type: "", Code: "", Value: "", ValueType: "" });
    setSelectedActivityCodes([]);
    setCurrentActivityFile(null);
    setActivityError("");
  };

  const handleEditActivity = (index: number) => {
    const actToEdit = activities[index];
    setEditingIndex(index);
    let teethArray: string[] = [];
    if (Array.isArray(actToEdit.toothNumbers)) {
      teethArray = actToEdit.toothNumbers;
    } else if (typeof actToEdit.toothNumbers === "string" && (actToEdit.toothNumbers as string).trim() !== "") {
      teethArray = (actToEdit.toothNumbers as string).split(",").map(s => s.trim()).filter(Boolean);
    }

    setCurrentActivity({
      ...actToEdit,
      toothNumbers: teethArray,
      currentToothInput: ""
    });
    setSelectedActivityCodes([{
      value: actToEdit.activityCode,
      label: actToEdit.activityCode,
      code: actToEdit.activityCode,
      realId: actToEdit.activityCode
    }]);
  };

  const removeActivity = (index: number) => {
    setActivities((prev) => 
      prev
        .filter((_, i) => i !== index)
        .map((act, i) => ({ ...act, ActivityId: String(i + 1) }))
    );
    if (editingIndex === index) {
      setEditingIndex(null);
      setCurrentActivity({ ...emptyActivity(), ActivityId: String(activities.length) });
      setSelectedActivityCodes([]);
    }
  };

  const handleGenerate = async () => {
    const isClaimIdMissing = !claimId || claimId.trim() === "";
    const isProviderIdMissing = !providerId || providerId.trim() === "";
    const isPrimaryDiagnosisMissing = !primaryDiagnosis; 
    const isActivitiesMissing = !activities || activities.length === 0;
    const isEncounterTypeMissing = encounterType === undefined || encounterType === null;
    
    if (
      isClaimIdMissing ||
      isProviderIdMissing ||
      isPrimaryDiagnosisMissing ||
      isActivitiesMissing ||
      isEncounterTypeMissing
    ) {
      const missingFields = [];
      if (isClaimIdMissing) missingFields.push("Claim ID");
      if (isProviderIdMissing) missingFields.push("Provider ID");
      if (isPrimaryDiagnosisMissing) missingFields.push("Primary Diagnosis");
      if (isActivitiesMissing) missingFields.push("At least one Activity");
      if (isEncounterTypeMissing) missingFields.push("Encounter Type");
  
      setResponseMessage(`⚠️ Please fill all required fields: ${missingFields.join(", ")}`);
      return; 
    }
  
    const rawBody = {
      claimId: claimId.trim(),
      ProviderID: providerId.trim(),
      patientIdentifierType: patientIdentifierType || undefined,
      patientIdentifierValue: patientIdentifierValue ? patientIdentifierValue.trim() : undefined,
      primaryDiagnosis: primaryDiagnosis.label.trim(),
      claimDate: claimDate ? toUtcIsoDate(claimDate) : undefined,
      encounterType: encounterType,
      tpaId: (tpaOption && tpaOption.length > 0) ? tpaOption[0].value : undefined, 
      payerId: (payerOption && payerOption.length > 0) ? payerOption[0].value : undefined,
      networkName: (network && network.length > 0) ? network[0].label : undefined,
      policyId: (policyIdOption && policyIdOption.length > 0) ? policyIdOption[0].label.trim() : undefined,
      policyName: (policyNameOption && policyNameOption.length > 0) ? policyNameOption[0].label.trim() : undefined,
      doctorLicenseNumber: (doctorLicenseNumber && doctorLicenseNumber.length > 0) ? doctorLicenseNumber[0].value.trim() : undefined,
      specialityName: (specialityName && specialityName.length > 0) ? specialityName[0].label.trim() : undefined,
      specialityCode: (specialityName && specialityName.length > 0) ? (specialityName[0].code || "").trim() : undefined,
      patientName: patientName || undefined,
      patientGender: patientGender || undefined,
      patientDateOfBirth: patientDateOfBirth ? toUtcDateOnly(patientDateOfBirth) : undefined,

      secondaryDiagnosis: (secondaryDiagnosis && secondaryDiagnosis.length > 0) 
        ? secondaryDiagnosis.map(d => d.label.trim()) 
        : undefined,
      
      activities: activities.map((a: any) => {
        let formattedToothNumbers: string[] | undefined = undefined;
        if (Array.isArray(a.toothNumbers)) {
          formattedToothNumbers = a.toothNumbers.map(String).filter(Boolean);
        }

        const activityObj = {
          ActivityId: a.ActivityId,
          activityCode: a.activityCode,
          activityType: a.activityType,
          quantity: a.quantity,
          preApprovalNumber: a.preApprovalNumber || undefined,
          hasSupportingDocuments: a.hasSupportingDocuments === true,
          toothNumbers: formattedToothNumbers && formattedToothNumbers.length > 0 ? formattedToothNumbers : undefined,
        };
        return Object.fromEntries(
          Object.entries(activityObj).filter(([_, value]) => value !== undefined && value !== "")
        );
      }),
    };

    const cleanedBody = Object.fromEntries(
      Object.entries(rawBody).filter(([_, value]) => {
        if (value === undefined || value === "") return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      })
    );

    const requestBody = [cleanedBody];
  
    try {
      setIsLoading(true); 
      setValidationResults([]); 
      setResponseMessage("");
      
      const response = await ClaimValidationAxiosInstance.post("claim-validation", requestBody, {
        headers: { 
          "Content-Type": "application/json", 
        },
      });
  
      const resDataArray = response.data?.data;
      const resData = Array.isArray(resDataArray) ? resDataArray[0] : resDataArray;

      if (resData) {
        const firstModule = resData.firstModuleChecks ?? [];
        const secondModuleActivities = resData.secondModuleChecks?.activityChecks ?? [];
        const secondModuleDiagnosis = resData.secondModuleChecks?.diagnosisChecks ?? [];

        const flattenedResults = [
          ...firstModule,
          ...secondModuleActivities,
          ...secondModuleDiagnosis
        ];

        setValidationResults(flattenedResults);
      }
      
      setResponseMessage(
        response.data.success 
          ? "Validation completed!" 
          : "Validation finished with some issues. Please review the results below."
      );
  
      if (typeof setCurrentStep === "function") {
        setCurrentStep(5);
      }
  
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errDataArray = error.response?.data?.data;
        const errData = Array.isArray(errDataArray) ? errDataArray[0] : errDataArray;
        if (errData) {
          const firstModule = errData.firstModuleChecks ?? [];
          const secondModuleActivities = errData.secondModuleChecks?.activityChecks ?? [];
          const secondModuleDiagnosis = errData.secondModuleChecks?.diagnosisChecks ?? [];
  
          const flattenedResults = [
            ...firstModule,
            ...secondModuleActivities,
            ...secondModuleDiagnosis
          ];
          setValidationResults(flattenedResults);
          
          if (typeof setCurrentStep === "function") {
            setCurrentStep(5);
          }
        } else {
          setValidationResults([
            { 
              validationSource: "System Error",
              source: "AXIOS",
              Status: "ERROR",
              Message: error.response?.data?.message || error.message 
            }
          ]);
          
          if (typeof setCurrentStep === "function") {
            setCurrentStep(5);
          }
        }
      } else {
        setResponseMessage("An unexpected error occurred.");
      }
    } finally { 
      setIsLoading(false); 
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
    setPatientName("");
    setPatientGender(undefined);             
    setPatientIdentifierType(undefined);     
    setPatientDateOfBirth("");
    setPatientIdentifierValue("");
    setClaimId("");
    setClaimDate("");                         
    setTpaOption([]);
    setPayerOption([]);
    setNetwork([]);
    setPolicyNameOption([]);
    setPolicyIdOption([]);
    setDoctorLicenseNumber([]);
    setSpecialityName([]);                     
    setEncounterType(1);
    setProviderId("");
    setPrimaryDiagnosis(null);                         
    setSecondaryDiagnosis([]);             
    setDiagnosisType("primary");
    setSelectedIcd([]);                        
    setDiagnosisError(null);
    setActivities([]);                         
    setCurrentActivity(emptyActivity());        
    setSelectedActivityCodes([]);              
    setCurrentObs({ Type: "", Code: "", Value: "", ValueType: "" });
    setActivityError("");
    setCurrentActivityFile(null);
    setValidationResults([]);
    setResponseMessage("");
    setEditingIndex(null);
  };

  const renderValidationCard = (validation: any, idx: number, claimIdVal: any) => {
    const status = (validation.Status || validation.status || "").toUpperCase();
    const cardTitle = validation.ruleName || validation.type || validation.validationSource || "Validation Check";
    const detailsText = validation.Message || validation.message || "";
    const source = validation.source || validation.validationSource || null;
    const activityCode = validation.activityCode || null;
    const diagnosisCode = validation.diagnosisCode || null;
    const activityId = validation.activityId || null;

    const isSuccess = status === "PASS" || status === "PASSED" || status === "SUCCESS";

    const TagRow = () => (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }}>
        {source && (
          <span className={styles.cvBadge} style={{ background: "#eef2fb", color: "#3b6fd4", fontWeight: "bold" }}>
            Source: {source}
          </span>
        )}
        {activityId && (
          <span className={styles.cvBadge} style={{ background: "#f3f4f6", color: "#374151", fontFamily: "var(--cv-mono)" }}>
            ID: {activityId}
          </span>
        )}
        {activityCode && (
          <span className={styles.cvBadge} style={{ background: "#f3f4f6", color: "#374151", fontFamily: "var(--cv-mono)" }}>
            Code: {activityCode} {validation.activityType ? `(${validation.activityType})` : ""}
          </span>
        )}
        {diagnosisCode && (
          <span className={styles.cvBadge} style={{ background: "#fdf2f8", color: "#9d174d", fontFamily: "var(--cv-mono)" }}>
            ICD: {diagnosisCode}
          </span>
        )}
        {status && (
          <span 
            className={styles.cvBadge} 
            style={{ 
              background: isSuccess ? "#f0fdf4" : "#fef2f2", 
              color: isSuccess ? "#16a34a" : "#dc2626", 
              fontWeight: "bold" 
            }}
          >
            {status}
          </span>
        )}
      </div>
    );

    return (
      <div 
        key={`card-${idx}`} 
        className={`${styles.cvVcard} ${isSuccess ? styles.cvVcardSuccess : styles.cvVcardFail}`} 
        style={
          isSuccess 
            ? { borderColor: "#22c55e", backgroundColor: "#f0fdf4" } 
            : { borderColor: "#ef4444", backgroundColor: "#fef2f2" }
        }
        data-testid={`validation-card-${idx}`}
      >
        <div className={styles.cvVcardHeader} style={{ cursor: "default" }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>{isSuccess ? "✅" : "❌"}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: "var(--cv-text)" }}>
              {cardTitle}
            </span>
            <TagRow />
            {detailsText && (
              <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--cv-muted)", lineHeight: "1.4" }}>
                {detailsText}
              </p>
            )}

            {validation.relatedActivities && validation.relatedActivities.length > 0 && (
              <div style={{ marginTop: 8, padding: "6px 10px", background: "#f9fafb", borderRadius: 4, borderLeft: "3px solid #3b6fd4" }}>
                <span style={{ fontSize: 11, fontWeight: "bold", color: "#374151" }}>Related Required Codes:</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                  {validation.relatedActivities.map((rel: any, ri: number) => (
                    <span key={ri} className={styles.cvBadge} style={{ background: "#ffffff", border: "1px solid #e5e7eb", color: "#4b5563" }}>
                      {rel.activityType}: {rel.activityCode}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.cvWrap} style={{ padding: "2px 24px", background: "var(--cv-bg)", minHeight: "100vh" }} data-testid="ClaimValidation-page">
      <VariablesInjector />
      <div className={styles.cvCard} data-testid="ClaimValidation-page-container">
        <div className={styles.cvPageHeader} style={{ marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid var(--cv-border)" }}>
          <h1 data-testid="ClaimValidation-page-title">Claim Validation</h1>
        </div>
 
        {/* Steps Indicator */}
        <div className={styles.cvSteps} data-testid="ClaimValidation-steps-indicator">
          <div className={styles.cvStepsTrack}>
            <div className={styles.cvStepsLine}>
              <div className={styles.cvStepsProgress} style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }} />
            </div>
            {stepNames.map((name, i) => {
              const stepNum = i + 1;
              return (
                <div 
                  key={stepNum} 
                  className={`${styles.cvStep} ${currentStep === stepNum ? styles.active : currentStep > stepNum ? styles.done : ""}`} 
                  onClick={() => handleStepClick(stepNum)}
                  data-testid={`ClaimValidation-step-${stepNum}`}
                >
                  <div className={styles.cvStepCircle}>{stepNum}</div>
                  <span className={styles.cvStepLabel}>{name}</span>
                </div>
              );
            })}
          </div>
        </div>
 
        {/* ── Step 1: Patient info ── */}
        {currentStep === 1 && (
          <div className={styles.cvSection} data-testid="ClaimValidation-step1-section">
            <h2 className={styles.cvSectionTitle}>Claim & Patient Information</h2>
            <div className={styles.cvGrid}>
              <div className={styles.cvField} data-testid="ClaimValidation-field-patientName">
                <CvLabel>
                  Patient  <span style={{ fontSize: "12px", color: "var(--cv-muted, #888)", fontWeight: "normal", marginLeft: "4px" }}>(Optional)</span>
                </CvLabel>
                <CvInput value={patientName} onChange={(e) => setPatientName(e.target.value)} placeholder="Full name" />
              </div>
              <div className={styles.cvField} data-testid="ClaimValidation-field-claimId">
                <CvLabel>Claim ID<span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span></CvLabel>
                <CvInput value={claimId} onChange={(e) => setClaimId(e.target.value)} placeholder="e.g. 510" />
              </div>
              <div className={styles.cvField} data-testid="ClaimValidation-field-claimDate">
                <CvLabel>Claim date <span style={{ fontSize: "12px", color: "var(--cv-muted, #888)", fontWeight: "normal", marginLeft: "4px" }}>(Optional)</span></CvLabel>
                <CvInput type="date" value={claimDate} onChange={(e) => setClaimDate(e.target.value)} />
              </div>
              <div className={styles.cvField} data-testid="ClaimValidation-field-gender">
                <CvLabel>Gender <span style={{ fontSize: "12px", color: "var(--cv-muted, #888)", fontWeight: "normal", marginLeft: "4px" }}>(Optional)</span></CvLabel>
                <CvSelect 
                  value={patientGender || ""} 
                  onChange={(e) => setPatientGender(e.target.value === "" ? undefined : (e.target.value as any))}
                >
                  <option value="">Select Gender...</option>
                  <option value="FEMALE">Female</option>
                  <option value="MALE">Male</option>
                </CvSelect>
              </div>
              
              <div className={styles.cvField} data-testid="ClaimValidation-field-dob">
                <CvLabel>Date of birth<span style={{ fontSize: "12px", color: "var(--cv-muted, #888)", fontWeight: "normal", marginLeft: "4px" }}>(Optional)</span></CvLabel>
                <CvInput type="date" value={patientDateOfBirth} onChange={(e) => setPatientDateOfBirth(e.target.value)} />
              </div>
              <div className={styles.cvField} data-testid="ClaimValidation-field-identifierType">
                <CvLabel>
                  Identifier type <span style={{ fontSize: "12px", color: "var(--cv-muted, #888)", fontWeight: "normal", marginLeft: "4px" }}>(Optional)</span>
                </CvLabel>
                <CvSelect 
                  value={patientIdentifierType || ""} 
                  onChange={(e) => setPatientIdentifierType(e.target.value === "" ? undefined : (e.target.value as any))}
                >
                  <option value="">Select Type...</option>
                  <option value="NATIONAL_ID">National ID</option>
                  <option value="PASSPORT">Passport</option>
                  <option value="MEMBER_ID">Member ID</option>
                  <option value="NEWBORN_CERTIFICATE">Newborn Certificate</option>
                </CvSelect>
              </div>
 
              <div className={styles.cvField} data-testid="ClaimValidation-field-identifierValue">
                <CvLabel>
                  Identifier value <span style={{ fontSize: "12px", color: "var(--cv-muted, #888)", fontWeight: "normal", marginLeft: "4px" }}>(Optional)</span>
                </CvLabel>
                <CvInput value={patientIdentifierValue} onChange={(e) => setPatientIdentifierValue(e.target.value)} placeholder="Enter identifier value" />
              </div>
              <div className={styles.cvField} data-testid="ClaimValidation-field-encounterType">
                <CvLabel>Encounter type <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span></CvLabel>
                <CvSelect 
                  value={String(encounterType)} 
                  onChange={(e) => setEncounterType(Number(e.target.value))}
                >
                  <option value="1">1 (OPD) - No Bed + No emergency room</option>
                  <option value="2">2 (OPD) - No Bed + Emergency room</option>
                  <option value="3">3 (IPD) - Inpatient Bed + No emergency room</option>
                  <option value="4">4 (IPD) - Inpatient Bed + Emergency room</option>
                  <option value="5">5 (Daycase) - Daycase Bed + No emergency room</option>
                  <option value="6">6 (Daycase) - Daycase Bed + Emergency room</option>
                  <option value="7">7 - Nationals Screening</option>
                  <option value="8">8 - New Visa Screening</option>
                  <option value="9">9 - Renewal Visa Screening</option>
                  <option value="12">12 - Home</option>
                  <option value="13">13 - Assisted Living Facility</option>
                  <option value="15">15 - Mobile Unit</option>
                  <option value="41">41 - Ambulance Land</option>
                  <option value="42">42 - Ambulance Air or Water</option>
                </CvSelect>
              </div>
            </div>
          </div>
        )}
 
        {/* ── Step 2: Insurance & Provider ── */}
        {currentStep === 2 && (
          <div className={styles.cvSection} data-testid="ClaimValidation-step2-section">
            <h2 className={styles.cvSectionTitle}>Insurance Information</h2>
            <div className={styles.cvGrid}>
              <div className={styles.cvField} data-testid="ClaimValidation-field-providerId">
                <CvLabel>Provider ID<span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span></CvLabel>
                <CvInput value={providerId} onChange={(e) => setProviderId(e.target.value)} placeholder="e.g. PRV-1234" />
              </div>
              <div className={styles.cvField} data-testid="ClaimValidation-field-tpa">
                <CvLabel>TPA:<span style={{ fontSize: "12px", color: "var(--cv-muted, #888)", fontWeight: "normal", marginLeft: "4px" }}>(Optional)</span></CvLabel>
                <ClaimCodeMultiSelect placeholder="Search..." value={tpaOption} onChange={setTpaOption} fetchOptions={fetchTpaOptions} />
              </div>
              <div className={styles.cvField} data-testid="ClaimValidation-field-payer">
                <CvLabel>Payer<span style={{ fontSize: "12px", color: "var(--cv-muted, #888)", fontWeight: "normal", marginLeft: "4px" }}>(Optional)</span></CvLabel>
                <ClaimCodeMultiSelect placeholder="Search..." value={payerOption} onChange={setPayerOption} fetchOptions={fetchPayerOptions} />
              </div>
              <div className={styles.cvField} data-testid="ClaimValidation-field-policyName">
                <CvLabel>Policy Name<span style={{ fontSize: "12px", color: "var(--cv-muted, #888)", fontWeight: "normal", marginLeft: "4px" }}>(Optional)</span></CvLabel>
                <ClaimCodeMultiSelect placeholder="Search by name..." value={policyNameOption} onChange={handlePolicyNameChange} fetchOptions={fetchPolicyNameOptions} />
              </div>
              <div className={styles.cvField} data-testid="ClaimValidation-field-policyId">
                <CvLabel>Policy ID<span style={{ fontSize: "12px", color: "var(--cv-muted, #888)", fontWeight: "normal", marginLeft: "4px" }}>(Optional)</span></CvLabel>
                <ClaimCodeMultiSelect placeholder="Search by ID..." value={policyIdOption} onChange={handlePolicyIdChange} fetchOptions={fetchPolicyIdOptions} />
              </div>
              <div className={styles.cvField} data-testid="ClaimValidation-field-network">
                <CvLabel>Network <span style={{ fontSize: "12px", color: "var(--cv-muted, #888)", fontWeight: "normal", marginLeft: "4px" }}>(Optional)</span></CvLabel>
                <ClaimCodeMultiSelect placeholder="Search Network ..." value={network} onChange={setNetwork} fetchOptions={fetchNetworkOptions} />
              </div>
              <div className={styles.cvField} data-testid="ClaimValidation-field-doctor">
                <CvLabel>Doctor<span style={{ fontSize: "12px", color: "var(--cv-muted, #888)", fontWeight: "normal", marginLeft: "4px" }}>(Optional)</span></CvLabel>
                <ClaimCodeMultiSelect placeholder="Search ..." value={doctorLicenseNumber} onChange={setDoctorLicenseNumber} fetchOptions={fetchDoctorOptions} />
              </div>
              <div className={styles.cvField} data-testid="ClaimValidation-field-specialty">
                <CvLabel>Specialty <span style={{ fontSize: "12px", color: "var(--cv-muted, #888)", fontWeight: "normal", marginLeft: "4px" }}>(Optional)</span></CvLabel>
                <ClaimCodeMultiSelect placeholder="Search ..." value={specialityName} onChange={setSpecialityName} fetchOptions={fetchSpecialityOptions} />
              </div>
            </div>
          </div>
        )}
 
        {/* ── Step 3: Diagnosis ── */}
        {currentStep === 3 && (
          <div className={styles.cvSection} data-testid="ClaimValidation-step3-section">
            <h2 className={styles.cvSectionTitle}>Diagnosis Information</h2>
            {diagnosisError && <div className={styles.cvAlertError} data-testid="ClaimValidation-diagnosis-error">⚠️ {diagnosisError}</div>}
            
            <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", marginBottom: "16px" }}>
              <div style={{ width: "150px" }} data-testid="ClaimValidation-diagnosis-type-wrap">
                <span style={{ fontSize: "12px", fontWeight: "bold", display: "block", marginBottom: "4px" }}>Diagnosis Type</span>
                <select 
                  data-testid="ClaimValidation-select-diagnosis-type"
                  value={diagnosisType} 
                  onChange={(e) => setDiagnosisType(e.target.value as "primary" | "secondary")}
                  style={{ width: "100%", height: "32px", borderRadius: "4px", borderColor: "#d9d9d9", padding: "0 8px" }}
                >
                  <option value="primary">Primary</option>
                  <option value="secondary">Secondary</option>
                </select>
              </div>
              <div style={{ flex: 1 }} data-testid="ClaimValidation-icd-search-wrap">
                <span style={{ fontSize: "12px", fontWeight: "bold", display: "block", marginBottom: "4px" }}>Search ICD Code</span>
                <ClaimCodeMultiSelect fetchOptions={fetchIcdCodes} value={selectedIcd} onChange={handleIcdChange} placeholder="Search by code or description (min 3 chars)..." />
              </div>
            </div>
 
            {(primaryDiagnosis || secondaryDiagnosis.length > 0) ? (
              <table className={styles.cvTable} style={{ marginBottom: 16 }} data-testid="ClaimValidation-diagnosis-table">
                <thead>
                  <tr>
                    <th>Diagnosis code</th>
                    <th>Type</th>
                    <th style={{ width: "100px", textAlign: "center" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {primaryDiagnosis && (
                    <tr data-testid="ClaimValidation-primary-diagnosis-row">
                      <td className={styles.cvMono}>{primaryDiagnosis.label}</td>
                      <td><span className={styles.cvBadge} style={{ background: "#eef2fb", color: "#3b6fd4", padding: "4px 8px", borderRadius: "4px" }}>Primary</span></td>
                      <td style={{ textAlign: "center" }}>
                        <button data-testid="ClaimValidation-btn-delete-primary-diagnosis" className={`${styles.cvBtn} ${styles.cvBtnDanger}`} onClick={() => {
                          setPrimaryDiagnosis(null);
                          setDiagnosisType("primary");
                        }}>Delete</button>
                      </td>
                    </tr>
                  )}
                  {secondaryDiagnosis.map((d, i) => (
                    <tr key={d.value} data-testid={`ClaimValidation-secondary-diagnosis-row-${i}`}>
                      <td className={styles.cvMono}>{d.label}</td>
                      <td><span className={styles.cvBadge} style={{ background: "#fffbeb", color: "#b45309", padding: "4px 8px", borderRadius: "4px" }}>Secondary</span></td>
                      <td style={{ textAlign: "center" }}>
                        <button data-testid={`ClaimValidation-btn-delete-secondary-diagnosis-${i}`} className={`${styles.cvBtn} ${styles.cvBtnDanger}`} onClick={() => setSecondaryDiagnosis(prev => prev.filter((_, idx) => idx !== i))}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ textAlign: "center", padding: "20px", background: "#f9f9f9", borderRadius: "6px", color: "#888", marginBottom: "16px" }} data-testid="ClaimValidation-no-diagnosis-msg">
                No diagnoses added yet. Use the search above to add codes.
              </div>
          )}
          </div>
        )}
 
        {/* ── Step 4: Activities ── */}
        {currentStep === 4 && (
          <div className={styles.cvSection} data-testid="ClaimValidation-step4-section">
            <h2 className={styles.cvSectionTitle}>Activity data</h2>
            {activityError && <div className={styles.cvAlertError} style={{ marginBottom: 12 }} data-testid="ClaimValidation-activity-error">⚠️ {activityError}</div>}
             
            <div className={styles.cvGrid}>
               <div className={styles.cvField} data-testid="ClaimValidation-field-activityId">
              <CvLabel>Activity ID</CvLabel>
              <CvInput 
                value={editingIndex !== null ? activities[editingIndex]?.ActivityId : String(activities.length + 1)} 
                disabled 
              />
             </div>
            <div className={styles.cvField} data-testid="ClaimValidation-field-activityType">
               <CvLabel>Activity type</CvLabel>
               <ActivityTypeSingleSelector 
                 value={currentActivity.activityType ? (CODE_TO_ACTIVITY_TYPE[String(currentActivity.activityType)] as AllowedActivityType) : undefined}
                 placeholder="Select Type..."
                 onChange={(selectedTypeVal) => {
                   const typeName = selectedTypeVal as string;
                   const numericType = typeName ? ACTIVITY_TYPE_TO_CODE[typeName as AllowedActivityType] || 0 : 0;
                   setSelectedActivityCodes([]);
                   setCurrentActivity((prev: any) => ({ 
                     ...prev, 
                     activityType: numericType ? Number(numericType) : 0, 
                     activityCode: "",
                     toothNumbers: typeName && typeName.toLowerCase() === "dental" ? prev.toothNumbers : [] 
                   }));
                 }}
              />
            </div>
            <div className={styles.cvField} style={{ gridColumn: "span 2" }} data-testid="ClaimValidation-field-activityCode">
               <CvLabel>Activity code</CvLabel>
               <ClaimCodeMultiSelect 
                 fetchOptions={fetchActivityCodes} 
                 value={selectedActivityCodes} 
                 onChange={handleActivityCodeChange} 
                 minChars={CODE_TO_ACTIVITY_TYPE[String(currentActivity.activityType)] === "Service" ? 1 : 3}
                 placeholder={currentActivity.activityType ? "Search codes..." : "Please select Activity Type first"} 
                 disabled={!currentActivity.activityType} 
              />
            </div>
              
            {CODE_TO_ACTIVITY_TYPE[String(currentActivity.activityType)]?.toLowerCase() === "dental" && (
              <div className={styles.cvField} style={{ gridColumn: "span 2" }} data-testid="ClaimValidation-field-toothNumbers">
                <CvLabel>
                  Tooth Numbers <span style={{ fontSize: "11px", color: "#888", fontWeight: "normal" }}>(Optional)</span>
                </CvLabel>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <CvInput 
                      placeholder="Enter a tooth number" 
                      value={currentActivity.currentToothInput || ""} 
                      onChange={(e) => setCurrentActivity((prev: any) => ({ ...prev, currentToothInput: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToothNumber(); } }}
                    />
                  </div>
                  <button 
                    type="button" 
                    className={`${styles.cvBtn} ${styles.cvBtnOutline}`} 
                    style={{ padding: "8px 16px", height: "38px" }}
                    onClick={addToothNumber}
                  >
                    + Add
                  </button>
                </div>
                {Array.isArray(currentActivity.toothNumbers) && currentActivity.toothNumbers.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
                    {currentActivity.toothNumbers.map((tooth: string, tIdx: number) => (
                      <span 
                        key={tIdx} 
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          background: "#eef2fb",
                          color: "#3b6fd4",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: 500
                        }}
                      >
                        {tooth}
                        <button 
                          type="button" 
                          onClick={() => removeToothNumber(tooth)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: "14px", padding: 0, lineHeight: 1 }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

          <div className={styles.cvField} data-testid="ClaimValidation-field-quantity">
            <CvLabel>Quantity</CvLabel>
            <CvInput type="number" placeholder="1" value={currentActivity.quantity === 0 ? "" : currentActivity.quantity} onChange={(e) => {
              const rawValue = e.target.value;
              if (rawValue === "") {
                setCurrentActivity((prev: any) => ({ ...prev, quantity: 0 }));
                return;
              }
              const val = Number(rawValue);
              setCurrentActivity((prev: any) => ({ ...prev, quantity: val >= 0 ? val : 1 }));
            }} />
          </div>
          <div className={styles.cvField} data-testid="ClaimValidation-field-preApprovalNo">
            <CvLabel>Pre-Approval No <span style={{ fontSize: "11px", color: "#888", fontWeight: "normal" }}>(Optional)</span></CvLabel>
            <CvInput placeholder="e.g. APP-9871" value={currentActivity.preApprovalNumber} onChange={(e) => setCurrentActivity((prev: any) => ({ ...prev, preApprovalNumber: e.target.value }))} />
          </div>

          <div className={styles.cvField} data-testid="ClaimValidation-field-supportingDoc">
            <CvLabel>
              Supporting Document <span style={{ fontSize: "11px", color: "#888", fontWeight: "normal" }}>(Optional)</span>
            </CvLabel>
           
            {!currentActivityFile ? (
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 14px",
                  border: "1px dashed #cbd5e1",
                  borderRadius: "8px",
                  backgroundColor: "#f8fafc",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#2563eb")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#cbd5e1")}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <span style={{ fontSize: 13, color: "#475569" }}>
                    Click to upload <span style={{ color: "#94a3b8" }}>(PDF, PNG, JPG)</span>
                  </span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 500, color: "#2563eb" }}>Browse</span>
                 
                <input
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleActivityFileChange}
                  data-testid="ClaimValidation-input-file-upload"
                />
              </label>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 14px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  backgroundColor: "#f8fafc",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 500, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                    {currentActivityFile.name}
                  </span>
                </div>
         
                <button
                  type="button"
                  onClick={clearActivityFile}
                  title="Remove file"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#94a3b8",
                    padding: 4,
                    display: "flex",
                    alignItems: "center",
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#ef4444")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
                  data-testid="ClaimValidation-btn-remove-file"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
 
        <div className={styles.cvActionsRow} style={{ marginTop: 20, display: "flex", gap: "10px" }}>
          <button 
            data-testid="ClaimValidation-btn-add-activity" 
            className={`${styles.cvBtn} ${styles.cvBtnAdd}`} 
            onClick={addActivityToTable}
          >
            {editingIndex !== null ? "💾 Update Activity" : "+ Add activity"}
          </button>
          {editingIndex !== null && (
            <button 
              className={`${styles.cvBtn} ${styles.cvBtnOutline}`} 
              onClick={() => {
                setEditingIndex(null);
                setCurrentActivity(emptyActivity());
                setSelectedActivityCodes([]);
              }}
            >
              Cancel Edit
            </button>
          )}
        </div>
 
        {activities.length > 0 ? (
          <table className={styles.cvTable} style={{ marginTop: 20 }} data-testid="ClaimValidation-activities-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Type</th>
                <th>Code</th>
                <th>Tooth</th>
                <th>Qty</th>
                <th>Pre-Approval</th>
                <th>Docs</th>
                <th style={{ width: "150px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {activities.map((act: any, i: number) => (
                <tr key={act.ActivityId || i} data-testid={`ClaimValidation-activity-row-${i}`}>
                  <td>{act.ActivityId && act.ActivityId.trim() ? act.ActivityId : (i + 1)}</td>
                  <td>
                    <span className={styles.cvBadge} style={{ background: "#f3f4f6", color: "#374151" }}>
                      {CODE_TO_ACTIVITY_TYPE[String(act.activityType)] || act.activityType}
                    </span>
                  </td>
                  <td className={styles.cvMono}>{act.activityCode}</td>
                  <td>
                    {Array.isArray(act.toothNumbers) && act.toothNumbers.length > 0 
                      ? act.toothNumbers.join(", ") 
                      : <span style={{ color: "#bbb" }}>—</span>}
                  </td>
                  <td>{act.quantity}</td>
                  <td>{act.preApprovalNumber || <span style={{ color: "#bbb" }}>—</span>}</td>
                  <td>
                    {act.hasSupportingDocuments ? (
                      <span className={styles.cvBadge} style={{ background: "#f0fdf4", color: "#16a34a" }}>Yes</span>
                    ) : (
                      <span className={styles.cvBadge} style={{ background: "#f3f4f6", color: "#6b7280" }}>No</span>
                    )}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <div style={{ display: "inline-flex", gap: "6px", alignItems: "center" }}>
                      <button 
                        className={`${styles.cvBtn} ${styles.cvBtnOutline}`} 
                        style={{ padding: "4px 10px", fontSize: "12px", height: "auto" }}
                        onClick={() => handleEditActivity(i)}
                      >
                        Edit
                      </button>
                      <button 
                        data-testid={`ClaimValidation-btn-delete-activity-${i}`} 
                        className={`${styles.cvBtn} ${styles.cvBtnDanger}`} 
                        style={{ padding: "4px 10px", fontSize: "12px", height: "auto" }}
                        onClick={() => removeActivity(i)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ textAlign: "center", padding: "20px", background: "#f9f9f9", borderRadius: "6px", color: "#888", marginTop: "20px" }} data-testid="ClaimValidation-no-activities-msg">
            No activities added yet. Fill the fields above to add activities to this claim.
          </div>
        )}
      </div>
    )}
 
    {/* ── Step 5: Results ── */}
    {currentStep === 5 && (
      <div className={styles.cvSection} data-testid="ClaimValidation-step5-section">
        <h2 className={styles.cvSectionTitle}>Validation Results</h2>
        {isLoading ? (
          <div style={{ textAlign: "center", padding: 40 }} data-testid="ClaimValidation-loading-state">
            <div className={styles.cvSpinner} style={{ margin: "0 auto 16px" }} />
            <p>Processing claim validation...</p>
          </div>
        ) : (
          <div data-testid="ClaimValidation-results-container">
            {validationResults.length === 0 ? (
              <p style={{ textAlign: "center", color: "var(--cv-muted)", padding: 20 }} data-testid="ClaimValidation-no-errors-msg">
                No validation errors found! All checks passed.
              </p>
            ) : (
              validationResults.map((rg: any, gi: number) => (
                <div key={gi} className={styles.cvResultGroup} data-testid={`ClaimValidation-result-group-${gi}`}>
                  {renderValidationCard(rg, gi, claimId)} 
                </div>
              ))
            )}
          </div>
        )}
      </div>
    )}
 
    {/* Navigation */}
    <div className={styles.cvNav} data-testid="ClaimValidation-navigation-bar">
      <button data-testid="ClaimValidation-btn-reset" className={styles.cvBtn} style={{ background: "#fef2f2", color: "#dc2626" }} onClick={handleReset}>↺ Reset</button>
      <div style={{ display: "flex", gap: 10 }}>
        <button data-testid="ClaimValidation-btn-previous" className={`${styles.cvBtn} ${styles.cvBtnOutline}`} onClick={handlePrevious} disabled={currentStep === 1 || isLoading}>← Previous</button>
        {currentStep < 4 ? (
          <button data-testid="ClaimValidation-btn-next" className={`${styles.cvBtn} ${styles.cvBtnPrimary}`} onClick={handleNext}>Next →</button>
        ) : currentStep === 4 ? (
          <button data-testid="ClaimValidation-btn-validate" className={`${styles.cvBtn} ${styles.cvBtnPrimary}`} onClick={handleGenerate} disabled={isLoading}>
            {isLoading ? "Validating..." : "✓ Validate Claim"}
          </button>
        ) : null}
      </div>
    </div>
 
   {responseMessage && (
     <div 
       className={(responseMessage.includes("failed") || responseMessage.includes("Please") || responseMessage.includes("⚠️")) ? styles.cvAlertFail : styles.cvAlertSuccess} 
       data-testid="ClaimValidation-response-message"
     >
       {responseMessage}
     </div>
   )}
 </div>
 <Toaster data-testid="ClaimValidation-page-toaster" />
</div>
  );
};

export default ClaimValidation;